// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import shallowEqual from 'shallowequal';

// Singletons
import zoomStateSingleton from '../../../canvas/zoom/zoomStateSingleton';

// Measurements
import Measure from '../../../../node_module_clones/react-measure/src/Measure';
import measurementsConnect from '../measurementsConnect';
import measurementsRegistry from '../measurementsRegistry';
import { syncedMeasurements } from './syncedMeasurementsSingleton';
import { measurementsSet, measurementsRemove } from './elementMeasurementsActions';

// Actions
import { checkCollisions } from '../../collision/elementCollisionActions';
import { getElementChildrenIdsThunk } from '../../../element/selectors/elementTraversalSelector';

// Utils
import logger from '../../../utils/logger';
import { prop } from '../../../../common/utils/immutableHelper';
import { collisionDetectionManager } from '../../collision/collisionDetectionManager';
import { getTopLeft, getRectCenterPoint, asRect } from '../../../../common/maths/geometry/rect';
import { unTransformMeasurementsInPlace } from './utils/measureElementZoomUtils';
import * as pointLib from '../../../../common/maths/geometry/point';
import { isHeightRestricted } from '../../../element/card/cardSizeUtil';
import { isColumn, isCommentThread, isAnnotation, isLine } from '../../../../common/elements/utils/elementTypeUtils';
import { isIconViewLike } from '../../../../common/elements/utils/elementDisplayUtils';
import { displayCommentAsCollapsed } from '../../../../common/comments/commentModelUtil';
import { isLocationCanvas } from '../../../../common/elements/utils/elementLocationUtils';
import {
    getElementId,
    getElementLocation,
    getElementType,
    getLocationSection,
    getXPosition,
    getYPosition,
} from '../../../../common/elements/utils/elementPropertyUtils';

/**
 * Not sure why, but modifying this in place appears to be necessary!
 * Doing so, for some reason results in a second "measureElement" invocation, whereas
 * if using the rectLib's translate function the measureElement isn't invoked for the second
 * time, and the node is null the first time, so boards don't get the correct originOffset.
 *
 * Unfortunately I can't understand why that's happening at this stage.
 * It might be to do with the react-measure library using a reference to the old measurement
 * to see if a new measurement should be taken? Now we're modifying the old measurement so it
 * will cause it to re-measure.
 */
const shiftInPlace = (rect, offset) => {
    rect.x = (rect.x || rect.left) + offset.x;
    rect.y = (rect.y || rect.top) + offset.y;
    rect.top += offset.y;
    rect.bottom += offset.y;
    rect.left += offset.x;
    rect.right += offset.x;

    return rect;
};

const removeFeatureSuggestionsHeightInPlace = (measurements, node, gridSize) => {
    if (!node || !measurements) return measurements;

    const featureSuggestions = node.querySelector('.FeatureSuggestions');

    if (!featureSuggestions) return measurements;

    // The FeatureSuggestions element has a -0.2rem margin
    const featureSuggestionsHeight = featureSuggestions.getBoundingClientRect().height - 0.2 * gridSize;

    measurements.height = measurements.height - featureSuggestionsHeight;
    measurements.bottom = measurements.bottom - featureSuggestionsHeight;

    return measurements;
};

const getCollapsedCommentRect = (measurements) => {
    if (!measurements) return measurements;

    const newTop = measurements.top - measurements.height;
    const newLeft = measurements.left - measurements.width / 2;

    return {
        ...measurements,
        bottom: newTop + measurements.height,
        top: newTop,
        left: newLeft,
        right: newLeft + measurements.width,
        y: newTop,
        x: newLeft,
    };
};

/**
 * If the element is a board and we have a reference to its DOM node, find the board icon
 * and use that as the connection origin for lines.
 */
const getOriginRect = (element, measurements, node) => {
    if (!node) return measurements;

    if (isCommentThread(element) && displayCommentAsCollapsed(element)) return getCollapsedCommentRect(measurements);

    if (!isIconViewLike(element)) return measurements;

    // Get the board's icon to use as the connection origin
    const iconNode = node.querySelector('.icon-container');

    return iconNode ? iconNode.getBoundingClientRect() : measurements;
};

/**
 * If an element is irregularly shaped (e.g. a board is an inverted T shape) we can use
 * multiple rects to describe its perimeter.
 * This can then be used to find intersections more accurately.
 */
const getElementRects = (element, measurements, node, canvasOffset) => {
    const rects = [measurements];

    if (!node) return rects;

    if (isCommentThread(element) && displayCommentAsCollapsed(element)) return [getCollapsedCommentRect(measurements)];

    if (!isIconViewLike(element)) return rects;

    // Add the EditableTitle bounding rect to the rects array
    const titleContainerNode = node.querySelector('.title-container');

    if (!titleContainerNode) return rects;

    const titleBoundingRect = asRect(titleContainerNode.getBoundingClientRect());
    shiftInPlace(titleBoundingRect, canvasOffset);

    rects.push(titleBoundingRect);

    return rects;
};

/**
 * Gets the origin in terms of an offset in px from the top left point of the connection target's measurements.
 * This allows a different origin to be used for irregular shaped elements, while still keeping the connection boundary
 * around the perimeter of the element.  For example, we want to connect to the centre of board icons, but want the
 * connection boundary to include the text.
 *
 * @param element {Object} The element that this decorator is rendering.
 * @param measurements {Object} An object with top, left, width and height properties.
 * @param node {HTMLNode} If the node exists it will be used to determine the origin, otherwise the boundary
 *      measurements will be used.
 * @param scale {number} The current scale of the board.
 */
const getUnscaledOriginOffset = (element, measurements, node, scale = 1) => {
    const originRectMeasurements = getOriginRect(element, measurements, node);
    const originPx = getRectCenterPoint(originRectMeasurements);
    const boundaryTopLeftPoint = getTopLeft(measurements);

    const scaledOriginOffset = pointLib.reverseTranslate(boundaryTopLeftPoint, originPx);
    const unscaledOriginOffset = pointLib.scale(1 / scale, scaledOriginOffset);
    return pointLib.round(unscaledOriginOffset);
};

const mapDispatchToProps = (dispatch) => ({
    dispatchGetChildIds: (id) => dispatch(getElementChildrenIdsThunk([id])),
    dispatchCheckCollisions: (...args) => dispatch(checkCollisions(...args)),
});

const mapMeasurementsDispatchToProps = (dispatch) => ({
    dispatchMeasurementsSet: ({ id, measurements }) => dispatch(measurementsSet({ id, measurements })),
    dispatchMeasurementsRemove: ({ id }) => dispatch(measurementsRemove({ id })),
});

export default (DecoratedComponent) => {
    @connect(undefined, mapDispatchToProps)
    @measurementsConnect(undefined, mapMeasurementsDispatchToProps)
    class MeasureElementDecorator extends React.Component {
        constructor(props, context) {
            super(props, context);
            this.hasBeenMeasured = false;

            this.measureComponent = React.createRef();
            this.measureElementRef = React.createRef();

            this.previousMeasurements = null;
            this.previousGridSize = props.gridSize;
            // This will keep track of changes in browser zoom and prevent them
            // from triggering collision detection
            this.previousDevicePixelRatio = window.devicePixelRatio;
        }

        componentWillMount() {
            const { element } = this.props;
            const id = getElementId(element);
            measurementsRegistry.registerElement(id, this);
        }

        /**
         * NOTE: The ResizeObserver only invokes its "onResize / measure" function when the DIMENSIONS of the
         * measured element change, not its position (top / left).
         * Thus we need to manually trigger a measure whenever we know that the position of the element
         * has changed. Right now that's only when the element's location or index changes, because if the element
         * is in a column, it will handle forcing its children to re-measure (see the bottom of measureElement).
         */
        componentDidUpdate(prevProps) {
            const { element } = this.props;

            const forceReMeasure = getElementLocation(element) !== getElementLocation(prevProps.element);

            if (forceReMeasure) this.forceMeasure();
        }

        componentWillUnmount() {
            const { element, dispatchMeasurementsRemove } = this.props;
            const id = getElementId(element);
            measurementsRegistry.removeElement(id, this);

            // Needed to do this because on 'undo' of a target move the element gets mounted again before dismount
            // of the previous version. Thus the dismount was causing the mounted measurements to get cleared
            // incorrectly
            if (measurementsRegistry.getElementReferenceCount(id) < 1 && this.hasBeenMeasured) {
                dispatchMeasurementsRemove({ id });
            }
        }

        forceMeasure = () => {
            if (!this.measureComponent.current) return;

            this.measureComponent.current.measure();
        };

        /**
         * This function takes in the bounding rect of an element, and converts it to measurements based on the top left
         * of the Canvas Document. It is used by:
         *  - `MeasureElementDecorator.measureElement()` to update the measurement store
         *  - `MeasurementsRegistry.getElementMeasurementFromCanvasDocument()` to synchronously get measurements without
         *    updating the store
         */
        getElementMeasurementFromCanvasDocument = (elementBoundingRect) => {
            const measurements = elementBoundingRect;

            const {
                element,
                getContextZoomScale,
                getContextZoomTranslationPx,
                gridSize,
                boardSection: workspaceSection,
            } = this.props;

            const scale = getContextZoomScale();

            const node = this.measureElementRef.current;
            const originOffset = getUnscaledOriginOffset(element, measurements, node, scale);

            // Translate measurements based on the canvas scroll
            const canvasOffset = measurementsRegistry.getCanvasViewportTranslation();

            shiftInPlace(measurements, canvasOffset);
            removeFeatureSuggestionsHeightInPlace(measurements, node, gridSize);

            measurements.workspaceSection = workspaceSection;
            measurements.section = getLocationSection(element);
            measurements.elementType = getElementType(element);

            // Scale the measurements based on the Canvas's current scale
            const zoomState = {
                scale,
                translation: getContextZoomTranslationPx(),
            };
            unTransformMeasurementsInPlace(measurements, zoomState);

            const rects = getElementRects(element, measurements, node, canvasOffset);

            // Scale all measurements.
            rects.forEach((rect) => {
                // rects might include the original measurements object, so we don't want to scale it again
                if (rect === measurements) return;
                unTransformMeasurementsInPlace(rect, zoomState);
            });

            return { ...measurements, originOffset, rects };
        };

        measureElement = (measurements) => {
            // During zooms we don't want measurement changes to be saved to the store, because the
            // store won't be up-to-date with the correct zoom state and as such when translating and
            // scaling the measurements it won't be correct.
            if (zoomStateSingleton.getIsZooming()) {
                if (!!this.previousMeasurements) return;

                // If we haven't measured this element yet, then subscribe it to when the zoom ends and
                // re-measure the element
                zoomStateSingleton.registerZoomEndSubscriber(this.forceMeasure);

                return;
            }

            // If the element has no height, then don't record its measurement as it hasn't been rendered
            // correctly yet
            if (measurements.height === 0) return;

            // This is a hack to prevent full-screens from causing the canvas to keep growing.
            // Potentially be more strict here (i.e. only for link element types with a video media type) but
            // for now, keeping it general
            if (measurements.width === window.screen.width) return;

            const {
                currentBoardId,
                element,
                dispatchMeasurementsSet,
                dispatchGetChildIds,
                dispatchCheckCollisions,
                gridSize,
                isPreview,
                isEditable,
            } = this.props;

            const id = getElementId(element);
            const node = this.measureElementRef.current;

            measurements = this.getElementMeasurementFromCanvasDocument(measurements);

            // If the element measurements haven't changed since the last execution, don't update the store
            if (shallowEqual(this.previousMeasurements, measurements)) return;

            const currentY = getYPosition(element);
            const currentX = getXPosition(element);

            // If this element has changed height while it's being edited
            const shouldCheckCollisions =
                collisionDetectionManager.enabled &&
                !isPreview &&
                isEditable &&
                !!syncedMeasurements &&
                !isLine(element) &&
                !isAnnotation(element) &&
                // If the grid size changes, then don't bother checking collisions
                this.previousGridSize === gridSize &&
                isLocationCanvas(element) &&
                // Only trigger collision detection if the current zoom level of the browser
                // is consistent
                this.previousDevicePixelRatio === window.devicePixelRatio &&
                // If there's no previous measurements then this is a new card that's just been created
                (!this.previousMeasurements ||
                    // If a 3d card transition is still in progress the difference will be < 4px
                    (Math.abs(this.previousMeasurements.height - measurements.height) > 4 &&
                        // Don't trigger collision detection if the card is being moved
                        this.previousX === currentX &&
                        this.previousY === currentY));

            // First check for collisions before updating the measurements map
            // This is so we can determine the change in height of the new measurements
            if (shouldCheckCollisions) {
                const isNewCard = !this.previousMeasurements && !prop(id, syncedMeasurements);

                // Log the cause of the next attempt to perform collision detection
                if (collisionDetectionManager.blocked && !collisionDetectionManager.logged) {
                    collisionDetectionManager.logged = true;

                    const reason = isNewCard
                        ? 'new card'
                        : `height change of ${measurements.height - this.previousMeasurements.height}px`;

                    logger.warn(
                        'Attempted to perform collision detection while blocked. ' +
                            'BOARD ID: %s, ID: %s, TYPE: %s, REASON:',
                        currentBoardId,
                        id,
                        getElementType(element),
                        reason,
                    );
                }

                dispatchCheckCollisions({
                    elementId: id,
                    elementMeasurement: measurements,
                    currentMeasurements: syncedMeasurements,
                    isNewCard,
                });
            }

            this.hasBeenMeasured = true;
            dispatchMeasurementsSet({ id, measurements });

            // If the column has changed in measurements, we should force-measure the children
            if (isColumn(element)) {
                const childIds = dispatchGetChildIds(id);
                childIds.forEach((childId) => measurementsRegistry.forceElementMeasure(childId));
            }

            // This works in cooperation with the ElementContentVisibleObserver.
            // It specifies the size to render the blank element when it's off screen and "content-visible" is "auto"
            if (node && node.style) {
                node.style.containIntrinsicSize = `${measurements.width}px ${measurements.height}px`;
            }

            this.previousGridSize = gridSize;
            this.previousMeasurements = measurements;
            this.previousDevicePixelRatio = window.devicePixelRatio;
            this.previousX = currentX;
            this.previousY = currentY;
        };

        render() {
            const { documentMode, isEditing, isClipboardCut, isFocusedForegroundElement } = this.props;

            // If we're cutting an element don't even worry about rendering & measuring it
            if (isClipboardCut) return null;

            if (documentMode || isFocusedForegroundElement) return <DecoratedComponent {...this.props} />;

            // We don't want connected lines to keep jumping while editing cards that are height restricted
            // (because the cards expand when they're being edited)
            const shouldMeasure = !(isHeightRestricted(this.props.element) && isEditing);

            return (
                <Measure
                    ref={this.measureComponent}
                    whitelist={['width', 'height', 'top', 'left']}
                    shouldMeasure={shouldMeasure}
                    onMeasure={this.measureElement}
                >
                    <DecoratedComponent
                        {...this.props}
                        measureElementRef={this.measureElementRef}
                        forceMeasure={this.forceMeasure}
                    />
                </Measure>
            );
        }
    }

    MeasureElementDecorator.propTypes = {
        currentBoardId: PropTypes.string,
        element: PropTypes.object.isRequired,
        isDragging: PropTypes.bool,
        isClipboardCut: PropTypes.bool,
        dispatchMeasurementsSet: PropTypes.func,
        dispatchMeasurementsRemove: PropTypes.func,
        dispatchGetChildIds: PropTypes.func,
        dispatchCheckCollisions: PropTypes.func,
        documentMode: PropTypes.bool,
        isEditable: PropTypes.bool,
        isEditing: PropTypes.bool,
        isPreview: PropTypes.bool,
        isFocusedForegroundElement: PropTypes.bool,
        gridSize: PropTypes.number,
        boardSection: PropTypes.string,

        getContextZoomScale: PropTypes.func,
        getContextZoomTranslationPx: PropTypes.func,
    };

    return MeasureElementDecorator;
};
